import { z } from "zod";
import { ActionBundleSchema, ApprovalReceiptSchema, ClinicalActionSchema } from "./actions.js";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
import { PrescriptionExecutionPackageSchema } from "./prescriptions.js";
import { ReferralExecutionPackageSchema } from "./referral.js";
export const AcceptedActionFulfillmentSchema = z
    .object({
    fulfillmentId: IdSchema,
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
    provider: z.string().min(1).max(80),
    state: z.enum(["queued", "processing", "dispatched", "acknowledged", "failed", "cancelled"]),
    idempotencyKey: z.string().min(8).max(200),
    queuedAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    partnerReferenceId: z.string().min(1).max(160).optional(),
})
    .strict();
export const AcceptedActionExpectedEventSchema = z
    .object({
    expectedEventId: IdSchema,
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
    status: z.enum([
        "open",
        "waiting",
        "received",
        "matched",
        "assigned_for_review",
        "reviewed",
        "patient_contact_pending",
        "closure_pending",
        "closed",
        "overdue",
        "escalated",
        "cancelled",
        "cannot_complete",
    ]),
    version: z.number().int().positive(),
})
    .strict();
/**
 * Worker-only, lease-bound execution material. It is reconstructed from the authoritative
 * acceptance transaction rather than trusting an outbox payload to carry clinical content.
 */
export const AcceptedActionExecutionContextSchema = z
    .object({
    tenantId: IdSchema,
    transactionId: IdSchema,
    approvalReceiptId: IdSchema,
    bundleId: IdSchema,
    bundleVersion: z.number().int().positive(),
    bundleContentHash: Sha256Schema,
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
    acceptedAt: IsoDateTimeSchema,
    bundle: ActionBundleSchema,
    action: ClinicalActionSchema,
    approval: ApprovalReceiptSchema,
    fulfillment: AcceptedActionFulfillmentSchema,
    expectedEvent: AcceptedActionExpectedEventSchema,
    referralPackage: ReferralExecutionPackageSchema.optional(),
    prescriptionPackage: PrescriptionExecutionPackageSchema.optional(),
})
    .strict()
    .superRefine((context, issue) => {
    if (context.bundle.id !== context.bundleId ||
        context.bundle.version !== context.bundleVersion ||
        context.bundle.contentHash !== context.bundleContentHash) {
        issue.addIssue({
            code: "custom",
            path: ["bundle"],
            message: "Execution context bundle does not match its pinned acceptance binding",
        });
    }
    if (context.action.id !== context.actionId ||
        context.action.version !== context.actionVersion ||
        context.fulfillment.actionId !== context.actionId ||
        context.fulfillment.actionVersion !== context.actionVersion ||
        context.expectedEvent.actionId !== context.actionId ||
        context.expectedEvent.actionVersion !== context.actionVersion) {
        issue.addIssue({
            code: "custom",
            path: ["action"],
            message: "Execution context action bindings are inconsistent",
        });
    }
    if (context.approval.id !== context.approvalReceiptId ||
        context.approval.bundleId !== context.bundleId ||
        context.approval.bundleVersion !== context.bundleVersion ||
        context.approval.contentHash !== context.bundleContentHash ||
        !context.approval.approvedActionIds.includes(context.actionId)) {
        issue.addIssue({
            code: "custom",
            path: ["approval"],
            message: "Execution context approval is not bound to the accepted action",
        });
    }
    if (context.action.kind === "referral") {
        const pkg = context.referralPackage;
        const approvedAction = context.approval.approvedActions.find((candidate) => candidate.actionId === context.actionId);
        if (pkg === undefined) {
            issue.addIssue({
                code: "custom",
                path: ["referralPackage"],
                message: "Accepted referral execution requires an exact referral package",
            });
        }
        else if (pkg.tenantId !== context.tenantId ||
            pkg.patientId !== context.action.patientId ||
            pkg.encounterId !== context.action.encounterId ||
            pkg.transactionId !== context.transactionId ||
            pkg.approvalReceiptId !== context.approvalReceiptId ||
            pkg.bundleId !== context.bundleId ||
            pkg.bundleVersion !== context.bundleVersion ||
            pkg.actionId !== context.actionId ||
            pkg.actionVersion !== context.actionVersion ||
            pkg.fulfillmentId !== context.fulfillment.fulfillmentId ||
            pkg.expectedEventId !== context.expectedEvent.expectedEventId ||
            (context.action.payload.destinationMode === "automatic"
                ? pkg.routingAuthorization?.contentHash !==
                    context.action.payload.routingAuthorization?.contentHash ||
                    pkg.routingResolution?.selectedDestinationId !== pkg.destination.destinationId
                : pkg.destination.destinationId !== context.action.payload.destinationId) ||
            !equalIdentifierSets(pkg.attachments.map((attachment) => attachment.attachmentId), context.action.payload.attachmentIds) ||
            approvedAction?.version !== context.actionVersion ||
            approvedAction.contentHash !== pkg.exactActionHash) {
            issue.addIssue({
                code: "custom",
                path: ["referralPackage"],
                message: "Referral package is not bound to the exact accepted action",
            });
        }
    }
    else if (context.referralPackage !== undefined) {
        issue.addIssue({
            code: "custom",
            path: ["referralPackage"],
            message: "Only a referral action may carry a referral execution package",
        });
    }
    if (context.action.kind === "prescription") {
        const pkg = context.prescriptionPackage;
        const approvedAction = context.approval.approvedActions.find((candidate) => candidate.actionId === context.actionId);
        if (pkg === undefined) {
            issue.addIssue({
                code: "custom",
                path: ["prescriptionPackage"],
                message: "Accepted prescription execution requires an exact prescription package",
            });
        }
        else if (pkg.tenantId !== context.tenantId ||
            pkg.patientId !== context.action.patientId ||
            pkg.encounterId !== context.action.encounterId ||
            pkg.transactionId !== context.transactionId ||
            pkg.approvalReceiptId !== context.approvalReceiptId ||
            pkg.bundleId !== context.bundleId ||
            pkg.bundleVersion !== context.bundleVersion ||
            pkg.actionId !== context.actionId ||
            pkg.actionVersion !== context.actionVersion ||
            pkg.fulfillmentId !== context.fulfillment.fulfillmentId ||
            pkg.expectedEventId !== context.expectedEvent.expectedEventId ||
            pkg.destination.destinationId !== context.action.payload.pharmacyDestinationId ||
            JSON.stringify(pkg.prescription) !== JSON.stringify(context.action.payload) ||
            approvedAction?.version !== context.actionVersion ||
            approvedAction.contentHash !== pkg.exactActionHash) {
            issue.addIssue({
                code: "custom",
                path: ["prescriptionPackage"],
                message: "Prescription package is not bound to the exact accepted action",
            });
        }
    }
    else if (context.prescriptionPackage !== undefined) {
        issue.addIssue({
            code: "custom",
            path: ["prescriptionPackage"],
            message: "Only a prescription action may carry a prescription execution package",
        });
    }
});
function equalIdentifierSets(left, right) {
    if (left.length !== right.length)
        return false;
    if (new Set(left).size !== left.length || new Set(right).size !== right.length)
        return false;
    const expected = new Set(right);
    return left.every((value) => expected.has(value));
}
export const AcceptedActionExecutionTransitionSchema = z
    .object({
    tenantId: IdSchema,
    transactionId: IdSchema,
    fulfillmentId: IdSchema,
    expectedEventId: IdSchema,
    fulfillmentState: z.enum(["queued", "processing", "dispatched", "acknowledged", "failed"]),
    actionState: z.enum(["queued", "dispatched", "acknowledged", "failed"]),
    bundleState: z.enum(["pending_approval", "queued", "dispatched", "acknowledged", "failed"]),
    updatedAt: IsoDateTimeSchema,
})
    .strict();
export const AcceptedActionExecutionSafetyDecisionSchema = z
    .object({
    tenantId: IdSchema,
    transactionId: IdSchema,
    fulfillmentId: IdSchema,
    expectedEventId: IdSchema,
    decision: z.literal("allow"),
    checkedAt: IsoDateTimeSchema,
    checks: z
        .array(z.enum([
        "active_lease",
        "current_bundle_policy",
        "exact_approval",
        "current_authority",
        "current_destination",
        "current_attachment_bytes",
        "owned_expected_event",
        "current_medication_content",
        "exact_patient_safety_context",
        "certified_prescribing_route",
    ]))
        .min(7)
        .max(10),
})
    .strict();
//# sourceMappingURL=accepted-action-execution.js.map